import webApiClient from "./client";

/**
 * Auth API Client
 *
 * Handles authentication and registration operations:
 * - Username and school name availability checks
 * - User registration
 * - Login and context selection
 * - Organisation creation (post-registration step)
 *
 * NOTE: This module is distinct from src/auth/auth.js which manages token
 * storage and refresh. This module only handles HTTP calls to auth endpoints.
 * Do not store tokens here — that belongs in the calling page or auth context.
 */

export const authApi = {
    /**
     * Check whether an email is on file as a registered account or as a per-org
     * guest-shadow profile. Used by guest forms to prompt login (account match)
     * or name reconciliation (guest-shadow match with different typed name).
     * @param {string} email - Email address to check
     * @param {string} [orgId] - Optional org context; enables guest-shadow lookup for that org
     * @returns {Promise<{ hasAccount: boolean, hasGuestProfile: boolean, storedName: { firstName, lastName } | null }>}
     * @throws {Error} If the check fails
     */
    checkEmail: async (email, orgId) => {
        const params = new URLSearchParams({ email });
        if (orgId) params.set("orgId", orgId);
        const response = await webApiClient.get(`/api/v2/auth/check-email?${params.toString()}`);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to check email");
        }

        return response.data;
    },

    /**
     * Check if a username is available for registration
     * @param {string} username - Username to check
     * @returns {Promise<object>} { available: boolean, message: string }
     * @throws {Error} If check fails
     */
    checkUsername: async username => {
        const response = await webApiClient.get(
            `/api/v2/auth/check-username/${encodeURIComponent(username)}`
        );

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to check username availability");
        }

        return response.data;
    },

    /**
     * Check if a school/business name is available for registration
     * @param {string} name - School name to check
     * @returns {Promise<object>} { available: boolean, message: string }
     * @throws {Error} If check fails
     */
    checkSchoolName: async name => {
        const response = await webApiClient.get(
            `/api/v2/auth/check-school-name/${encodeURIComponent(name)}`
        );

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to check school name availability");
        }

        return response.data;
    },

    /**
     * Register a new user account
     * @param {object} data - Registration data
     * @param {string} data.firstName - First name
     * @param {string} data.lastName - Last name
     * @param {string} data.username - Chosen username
     * @param {string} data.email - Email address
     * @param {string} data.password - Password
     * @param {string[]} [data.favouriteDanceStyles] - Canonical slug ids of the styles the
     *   person themselves dances. Optional; the backend resolves them and drops anything
     *   unrecognised. Distinct from an organisation's businessDanceStyles.
     * @returns {Promise<object>} { success: boolean, message: string }
     * @throws {Error} If registration fails
     */
    register: async data => {
        const response = await webApiClient.post("/api/v2/auth/register", data);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to register");
        }

        return response.data;
    },

    /**
     * Log in with email and password
     * Returns tokens that the caller is responsible for storing.
     * @param {object} credentials - Login credentials
     * @param {string} credentials.email - Email address
     * @param {string} credentials.password - Password
     * @returns {Promise<object>} { accessToken, refreshToken, ... }
     * @throws {Error} If login fails or credentials invalid
     */
    login: async credentials => {
        const response = await webApiClient.post("/api/v2/auth/login", credentials);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to login");
        }

        return response.data;
    },

    /**
     * Select an organisation login context after initial authentication
     * Used when a user is associated with multiple organisations
     * @param {string} orgId - Organisation ID to log into
     * @returns {Promise<object>} { accessToken, ... } — new token scoped to selected org
     * @throws {Error} If context selection fails
     */
    selectLoginContext: async orgId => {
        const response = await webApiClient.post("/api/v2/auth/select-login-context", { orgId });

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to select login context");
        }

        return response.data;
    },

    /**
     * Create an organisation as part of the registration flow
     * Called after user account creation and initial login
     * @param {object} orgData - Organisation details
     * @param {string} orgData.name - Organisation name
     * @param {object} orgData.orgLocation - Location object
     * @param {boolean} orgData.isDanceSchool - Whether registered as a school
     * @param {boolean} orgData.isDanceArtist - Whether registered as an artist
     * @param {Array}  orgData.businessDanceStyles - Dance styles offered
     * @param {string} [orgData.businessBlurb] - Short description
     * @returns {Promise<object>} { success: boolean, message: string, ... }
     * @throws {Error} If creation fails
     */
    createOrg: async orgData => {
        const response = await webApiClient.post("/api/v2/org/create", orgData);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to create your account");
        }

        return response.data;
    },

    /**
     * Exchange the stored refresh token for a fresh access token. Used by the
     * standalone org-setup page after createOrg to mint a token carrying the new
     * organisationId (the page has no password to re-login with). The backend
     * re-reads organisationId from the auth document when issuing the token.
     * @param {string} refreshToken
     * @returns {Promise<object>} { success, accessToken, message }
     */
    refreshAccessToken: async refreshToken => {
        const response = await webApiClient.post("/api/v2/auth/refresh", { refreshToken });
        return response.data;
    },

    /**
     * Confirm email ownership from the emailed verification link (REG-4).
     * Public endpoint (no auth). Returns the flattened body on BOTH success and
     * failure instead of throwing — the verify page branches on `code`
     * (TOKEN_EXPIRED / TOKEN_INVALID) for its error states.
     * @param {string} token - 64-char hex token from the /verify-email/:token URL
     * @returns {Promise<object>} { ok, success, alreadyVerified?, code?, message }
     */
    verifyEmail: async token => {
        const response = await webApiClient.post("/api/v2/auth/verify-email", { token });
        return { ok: response.ok, ...(response.data || {}) };
    },

    /**
     * Request a fresh verification link. Public endpoint (no auth).
     * Backend is non-enumerating: always 200 with the same generic message
     * whether or not the address exists or is already verified.
     * @param {string} email
     * @returns {Promise<object>} { success, message }
     * @throws {Error} On network/validation failure
     */
    resendVerification: async email => {
        const response = await webApiClient.post("/api/v2/auth/resend-verification", { email });

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to request a new link");
        }

        return response.data;
    },
};
